import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ReactApexChart from 'react-apexcharts';
import Iconify from '../../../components/Iconify';
import Label from '../../../components/Label';

const fEur = (n) =>
  new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);

const safe = (v) => {
  if (v == null) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return v;
};

function MetricCard({ label, value, subline, hint, icon }) {
  return (
    <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        {icon && (
          <Stack
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1.5,
              bgcolor: 'primary.lighter',
              color: 'primary.main',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Iconify icon={icon} width={20} height={20} />
          </Stack>
        )}
        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="caption" color="text.secondary" noWrap>
              {label}
            </Typography>
            {hint && (
              <Tooltip title={hint}>
                <span style={{ display: 'inline-flex' }}>
                  <Iconify
                    icon="eva:info-outline"
                    width={14}
                    height={14}
                    sx={{ color: 'text.disabled', cursor: 'help' }}
                  />
                </span>
              </Tooltip>
            )}
          </Stack>
          <Typography variant="h5" noWrap>
            {value}
          </Typography>
          {subline && <Typography variant="caption">{subline}</Typography>}
        </Stack>
      </Stack>
    </Card>
  );
}

MetricCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  subline: PropTypes.node,
  hint: PropTypes.string,
  icon: PropTypes.string,
};

function CurrencyDonut({ title, totals }) {
  const theme = useTheme();
  const entries = Object.entries(totals).filter(([, v]) => v > 0);

  const labels = entries.map(([k]) => k.toUpperCase());
  const series = entries.map(([, v]) => Number(v.toFixed(2)));
  const total = series.reduce((s, v) => s + v, 0);

  const palette = [
    theme.palette.primary.main,
    theme.palette.warning.main,
    theme.palette.info.main,
    theme.palette.success.main,
    theme.palette.error.main,
    theme.palette.secondary.main,
  ];

  const options = {
    chart: { type: 'pie', fontFamily: theme.typography.fontFamily },
    labels,
    colors: palette.slice(0, Math.max(labels.length, 1)),
    legend: { show: false },
    dataLabels: {
      enabled: true,
      formatter: (val, opts) => `${labels[opts.seriesIndex]}\n${Math.round(val)}%`,
      style: { fontSize: '10px', fontWeight: 600, colors: ['#fff'] },
      dropShadow: { enabled: true, top: 0, left: 0, blur: 2, color: 'rgba(0,0,0,0.45)', opacity: 1 },
    },
    tooltip: { y: { formatter: (v) => fEur(v) } },
    noData: { text: 'Sin datos' },
  };

  return (
    <Card variant="outlined" sx={{ p: 1.5, height: '100%' }}>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ px: 0.5 }}>
        <Typography variant="caption" fontWeight={600}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
          {fEur(total)}
        </Typography>
      </Stack>
      {entries.length === 0 ? (
        <Box sx={{ py: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Sin datos
          </Typography>
        </Box>
      ) : (
        <ReactApexChart options={options} series={series} type="pie" height={140} />
      )}
    </Card>
  );
}

CurrencyDonut.propTypes = {
  title: PropTypes.string.isRequired,
  totals: PropTypes.object.isRequired,
};

export default function DebugPanel({ stats }) {
  // setOpen se quitó al ocultar el toggle de auditoría; reactivar junto al <Stack> comentado más abajo.
  const [open] = useState(false);

  const monthlyByCurrency = useMemo(() => {
    const out = {};
    (stats?.debug?.monthlyContributions || []).forEach((c) => {
      const cur = (c.subCurrency || '').toLowerCase() || 'unknown';
      out[cur] = (out[cur] || 0) + (Number(c.amountEUR) || 0);
    });
    return out;
  }, [stats]);

  const renewalsByCurrency = useMemo(() => {
    const out = {};
    (stats?.renewalsByMonth || []).forEach((m) => {
      (m?.items || []).forEach((it) => {
        const cur = (it.currency || '').toLowerCase() || 'unknown';
        out[cur] = (out[cur] || 0) + (Number(it.cycleAmountEUR) || 0);
      });
    });
    return out;
  }, [stats]);

  if (!stats) return null;

  const debug = stats.debug || {};
  const rates = stats.currencyRates || {};
  const contributions = debug.monthlyContributions || [];
  const renewalsByMonth = (stats.renewalsByMonth || []).filter((m) => Array.isArray(m?.items) && m.items.length > 0);
  const totalRenewalSubs = renewalsByMonth.reduce((sum, m) => sum + m.items.length, 0);
  const totalRenewalEUR = renewalsByMonth.reduce(
    (sum, m) => sum + m.items.reduce((s, it) => s + (Number(it.cycleAmountEUR) || 0), 0),
    0
  );

  const activeFromDebug = debug?.byStatus?.active;
  const activeStripeSubs =
    typeof activeFromDebug === 'number'
      ? activeFromDebug
      : (stats.activeMonthlySubscriptions || 0) + (stats.activeRenewalSubscriptions || 0);
  const activeSubs = activeStripeSubs + (Number(stats.activeOneTimeSubscriptions) || 0);
  const monthlyCancels = Array.isArray(stats.canceledMonthly)
    ? stats.canceledMonthly.length
    : Number(stats.monthlyCancelations) || 0;
  const annualCancels = Array.isArray(stats.canceledRenewals) ? stats.canceledRenewals.length : 0;
  const cancelingSubs = monthlyCancels + annualCancels;
  const renewingSubs = Math.max(activeSubs - cancelingSubs, 0);
  const seatsFromTopLevel =
    Number(stats.activeMonthlySeats || 0) +
    Number(stats.activeRenewalSeats || 0) +
    Number(stats.activeOneTimeSeats || 0);
  const activeSeats =
    seatsFromTopLevel > 0
      ? seatsFromTopLevel
      : (debug?.monthlyContributions || []).reduce((sum, c) => sum + (Number(c.qty) || 0), 0);
  const seatsBreakdown =
    seatsFromTopLevel > 0
      ? [
          ['mens.', stats.activeMonthlySeats],
          ['anuales', stats.activeRenewalSeats],
          ['únicas', stats.activeOneTimeSeats],
        ]
          .filter(([, v]) => Number(v) > 0)
          .map(([k, v]) => `${Number(v).toLocaleString('es-ES')} ${k}`)
          .join(' · ')
      : null;

  const cancelParts = [];
  if (monthlyCancels > 0) cancelParts.push(`${monthlyCancels.toLocaleString('es-ES')} mens.`);
  if (annualCancels > 0) cancelParts.push(`${annualCancels.toLocaleString('es-ES')} anuales`);

  const activeSubline =
    cancelingSubs > 0 ? (
      <>
        <Typography component="span" variant="caption" color="text.secondary">
          {renewingSubs.toLocaleString('es-ES')} renovarán
        </Typography>
        <Typography component="span" variant="caption" color="text.disabled" sx={{ mx: 0.5 }}>
          ·
        </Typography>
        <Typography component="span" variant="caption" sx={{ color: 'warning.main', fontWeight: 600 }}>
          {cancelParts.join(' + ')} cancelarán
        </Typography>
      </>
    ) : null;

  return (
    <Box sx={{ px: 2, pt: 1, pb: 0 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Subs activas"
            value={activeSubs.toLocaleString('es-ES')}
            subline={activeSubline}
            icon="eva:credit-card-outline"
            hint="Subs con status=active en Stripe (de stats.debug.byStatus.active)."
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Asientos activos"
            value={activeSeats.toLocaleString('es-ES')}
            subline={
              seatsBreakdown ? (
                <Typography component="span" variant="caption" color="text.secondary">
                  {seatsBreakdown}
                </Typography>
              ) : null
            }
            icon="eva:people-outline"
            hint="activeMonthlySeats + activeRenewalSeats + activeOneTimeSeats."
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <CurrencyDonut title="Mensuales / moneda (€ MRR)" totals={monthlyByCurrency} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <CurrencyDonut title="Anuales / moneda (€ ciclo)" totals={renewalsByCurrency} />
        </Grid>
      </Grid>

      {/* Auditoría has been hidden because it's not needed */}
      {/* <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        onClick={() => setOpen((v) => !v)}
        sx={{ cursor: 'pointer', mt: 2, mb: 2, userSelect: 'none' }}
      >
        <Iconify icon={open ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'} width={20} height={20} />
        <Typography variant="subtitle2" color="text.secondary">
          {open ? 'Ocultar auditoría' : 'Ver auditoría'}
        </Typography>
      </Stack> */}

      {open && (
        <>
          <Stack spacing={1.5} mb={3}>
            <Stack direction="row" spacing={2}>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 140 }}>
                Cuenta Stripe:
              </Typography>
              <Typography variant="body2">
                {safe(debug.account?.name)}{' '}
                <Typography component="span" variant="caption" color="text.disabled">
                  ({safe(debug.account?.id)})
                </Typography>
              </Typography>
            </Stack>
            <Stack direction="row" spacing={2}>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 140 }}>
                Suscripciones procesadas:
              </Typography>
              <Typography variant="body2">{safe(debug.totalFetched)}</Typography>
            </Stack>
            {debug.byStatus && (
              <Stack direction="row" spacing={2}>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 140 }}>
                  Por status:
                </Typography>
                <Typography variant="body2">
                  {Object.entries(debug.byStatus)
                    .map(([k, v]) => `${k}: ${safe(v)}`)
                    .join(' · ')}
                </Typography>
              </Stack>
            )}
            {debug.byInterval && (
              <Stack direction="row" spacing={2}>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 140 }}>
                  Por intervalo:
                </Typography>
                <Typography variant="body2">
                  {Object.entries(debug.byInterval)
                    .map(([k, v]) => `${k}: ${safe(v)}`)
                    .join(' · ')}
                </Typography>
              </Stack>
            )}

            {debug.manualTotalFetched != null && (
              <>
                <Stack direction="row" spacing={2}>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 140 }}>
                    Manuales procesadas:
                  </Typography>
                  <Typography variant="body2">
                    {safe(debug.manualTotalFetched)}
                    {debug.manualSkipped != null && (
                      <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 1 }}>
                        ({safe(debug.manualSkipped)} descartadas)
                      </Typography>
                    )}
                  </Typography>
                </Stack>
                {debug.manualByStatus && (
                  <Stack direction="row" spacing={2}>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 140 }}>
                      Manuales por status:
                    </Typography>
                    <Typography variant="body2">
                      {Object.entries(debug.manualByStatus)
                        .map(([k, v]) => `${k}: ${safe(v)}`)
                        .join(' · ')}
                    </Typography>
                  </Stack>
                )}
                {debug.manualByInterval && (
                  <Stack direction="row" spacing={2}>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 140 }}>
                      Manuales por intervalo:
                    </Typography>
                    <Typography variant="body2">
                      {Object.entries(debug.manualByInterval)
                        .map(([k, v]) => `${k}: ${safe(v)}`)
                        .join(' · ')}
                    </Typography>
                  </Stack>
                )}
              </>
            )}
          </Stack>

          {Object.keys(rates).length > 0 && (
            <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<Iconify icon="eva:chevron-down-fill" />}>
                <Typography variant="subtitle2">Tipos de cambio aplicados</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {Object.entries(rates)
                    .map(([k, v]) => `${k.toUpperCase()}: ${safe(v)}`)
                    .join(' · ')}
                </Typography>
              </AccordionDetails>
            </Accordion>
          )}

          {contributions.length > 0 && (
            <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<Iconify icon="eva:chevron-down-fill" />}>
                <Typography variant="subtitle2">Contribuciones al MRR ({contributions.length} subs)</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Fuente</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Producto</TableCell>
                        <TableCell>Moneda</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell align="right">€ MRR</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {contributions.map((c) => (
                        <TableRow key={String(c.id)} hover>
                          <TableCell>
                            <Label color={c.source === 'manual' ? 'default' : 'info'}>
                              {typeof c.source === 'string' ? c.source : 'stripe'}
                            </Label>
                          </TableCell>
                          <TableCell>{safe(c.customerEmail)}</TableCell>
                          <TableCell>{safe(c.productName)}</TableCell>
                          <TableCell>{typeof c.subCurrency === 'string' ? c.subCurrency.toUpperCase() : '—'}</TableCell>
                          <TableCell align="right">{safe(c.qty)}</TableCell>
                          <TableCell align="right">{fEur(c.amountEUR)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          )}

          {totalRenewalSubs > 0 && (
            <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<Iconify icon="eva:chevron-down-fill" />}>
                <Typography variant="subtitle2">
                  Contribuciones de renovaciones anuales ({totalRenewalSubs} subs · {fEur(totalRenewalEUR)} total)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  {renewalsByMonth.map((m) => {
                    const monthTotal = m.items.reduce((s, it) => s + (Number(it.cycleAmountEUR) || 0), 0);
                    return (
                      <Box key={m.month}>
                        <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                          <Typography variant="subtitle2">{safe(m.month)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {m.items.length} sub{m.items.length === 1 ? '' : 's'} · {fEur(monthTotal)}
                          </Typography>
                        </Stack>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Fuente</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Producto</TableCell>
                                <TableCell>Moneda</TableCell>
                                <TableCell align="right">Ciclo</TableCell>
                                <TableCell align="right">€ ciclo</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {m.items.map((it) => (
                                <TableRow key={String(it.id)} hover>
                                  <TableCell>
                                    <Label color={it.source === 'manual' ? 'default' : 'info'}>
                                      {typeof it.source === 'string' ? it.source : 'stripe'}
                                    </Label>
                                  </TableCell>
                                  <TableCell>{safe(it.customerEmail)}</TableCell>
                                  <TableCell>{safe(it.productName)}</TableCell>
                                  <TableCell>
                                    {typeof it.currency === 'string' ? it.currency.toUpperCase() : '—'}
                                  </TableCell>
                                  <TableCell align="right">
                                    {safe(it.intervalCount)} {safe(it.interval)}
                                    {it.intervalCount === 1 ? '' : 's'}
                                  </TableCell>
                                  <TableCell align="right">{fEur(it.cycleAmountEUR)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    );
                  })}
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}
        </>
      )}
    </Box>
  );
}

DebugPanel.propTypes = {
  stats: PropTypes.object,
};
